import {
  Box,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React from 'react';
import customConfig from 'src/config';
import { useTranslation } from 'react-i18next';

import TextEllipsis from '../../../components/Common/Tooltip/TextEllipsis';
import { extractEmail, isInUIC } from '@Utils/common';

export interface TableConfig {
  // use percent width xx% firstly
  // column min width size, sum(width) must <= 100%
  width?: string;
  // css flex-grow ratio
  // allocate space 100% - sum(width)
  // if you do not want to use grow, ensure sum(width) == 100%
  grow?: number;
  // left | right | center
  align?: string;
  // hover show tooltip
  tooltip?: boolean;
}

const TableTemplate = (props: {
  config: TableConfig[];
  header: any[];
  data: any[][];
  width?: string;
  height?: string;
  isRecommendConfig?: boolean;
  appendContextMenu?: {
    columnIndex: number;
    enabled: boolean;
    component: (user: string) => React.ReactNode;
  };
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { header, data, config } = props;
  return (
    <Table
      width={props.width || '100%'}
      height={props.height || '100%'}
      fontSize="14px"
    >
      <TableHeader
        pr="8px"
        background={colorMode === 'dark' ? '' : colorStyle.background.secondary}
      >
        <TableRow height="36px" width="100%">
          {header.map((data: any, i: number) => (
            <TableCell
              key={i}
              width={config[i].width || ''}
              flexGrow={config[i].grow ?? 1}
              color={colorStyle.color.secondary}
            >
              <Box width="90%">
                <TextEllipsis align={config[i].align}>{data}</TextEllipsis>
              </Box>
            </TableCell>
          ))}
        </TableRow>
      </TableHeader>
      {data.length > 0 ? (
        <TableBody overflowY="scroll">
          {data.map((data, i: number) => (
            <TableRow key={i} height="36px" width="100%">
              {data.map((item, i: number) => (
                <TableCell
                  key={i}
                  width={config[i].width || ''}
                  flexGrow={config[i].grow ?? 1}
                  display="flex"
                  alignItems="center"
                >
                  {isInUIC() &&
                  customConfig.ui.contextMenuEnabled &&
                  props.appendContextMenu?.enabled &&
                  props.appendContextMenu.columnIndex === i ? (
                    <Flex flexDirection="row" justifyContent="space-between">
                      <Box width="90%">
                        <TextEllipsis align={config[i].align}>
                          {item}
                        </TextEllipsis>
                      </Box>
                      <Box>
                        {props.appendContextMenu.component(extractEmail(item))}
                      </Box>
                    </Flex>
                  ) : (
                    <TextEllipsis align={config[i].align}>{item}</TextEllipsis>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      ) : (
        <NoData
          isRecommendConfig={!!props.isRecommendConfig}
          colorStyle={colorStyle}
        />
      )}
    </Table>
  );
};

function NoData({
  isRecommendConfig,
  colorStyle,
}: {
  isRecommendConfig: boolean;
  colorStyle: any;
}) {
  const { t } = useTranslation();
  return (
    <Flex
      direction="row"
      align="center"
      justify="center"
      marginTop="16px"
      fontSize="14px"
      color={colorStyle.color.secondary}
      marginRight={isRecommendConfig ? '15%' : '0px'}
    >
      {t('dashboard.noData')}
    </Flex>
  );
}

export default TableTemplate;
